// Onboarding intake (INTAKE=1): what brings her here. Browser safe: the member app, the API,
// the plan builder, the review page data, validate-content and the tests read this one list.
// Legal frame: wellness education. Topics are everyday words she taps, never a diagnosis.
// Topics reach lessons and the starting plan only. They never reach supplements, Marie, a model or the OS.
import { SCAN_AREAS } from './scan-areas.mjs';

export const INTAKE_MAX_TOPICS = 3;
export const INTAKE_NOTE_MAX = 500;
/** A lesson says "For you" when it is in her topic's own list or in the first this many of the area's lesson pool. */
export const HIGHLIGHT_POOL_TOP = 6;

/** `phrase` is what the "You said …" sentence says for this topic. It defaults to the label in lower case. */
const topic = (id, label, area, lessons, phrase = label.toLowerCase()) => Object.freeze({ id, label, area, lessons: Object.freeze(lessons), phrase });
/** The everyday topics she can pick. Order matters: her picks are stored and planned in this order. Each maps to ONE scan area. */
export const INTAKE_TOPICS = Object.freeze([
  topic('sleep', 'Sleep', 'sleep', ['tsleep01', 'tsleep02', 'tsleep03', 'tsleep06', 'tsleep11']),
  topic('stress', 'Stress', 'stress', ['tcalm01', 'tcalm04', 'tcalm05', 'tcalm07', 'tcalm10']),
  topic('mood', 'Mood', 'mood', ['tcalm10']),
  topic('energy', 'Energy', 'energy', ['tenergy01', 'tenergy02', 'tenergy03', 'tenergy05', 'tenergy07']),
  topic('weight', 'Weight', 'energy', ['twt01', 'twt02', 'tenergy11', 'tenergy06', 'm31b']),
  topic('bloodSugar', 'Blood sugar', 'energy', ['tenergy02', 'm31b']),
  topic('bloodPressure', 'Blood pressure', 'movement', ['tmove11', 'tnourish09', 'thormones10', 'm31a']),
  topic('heart', 'Heart health', 'movement', ['tmove11', 'tnourish09']),
  topic('gut', 'Gut health', 'digestion', ['tgut02', 'tgut03', 'tgut08', 'tgut11']),
  topic('cycle', 'Monthly cycle', 'hormones', ['tgut08', 'tenergy08', 'thormones02', 'thormones03']),
  topic('menopause', 'Menopause', 'hormones', ['thormones05', 'thormones06', 'thormones07', 'thormones08', 'tsleep07', 'm25b']),
  topic('thyroid', 'Thyroid', 'hormones', ['thormones04', 'thyr01', 'thyr02'], 'your thyroid'),
  topic('hormones', 'Hormone health', 'hormones', ['thh01', 'thormones01', 'thormones12']),
  topic('postpartum', 'Postpartum', 'rest', ['tpp01', 'tpp02', 'tpp03', 'tmove07', 'tsleep10', 'tcalm10'], 'postpartum recovery'),
  topic('autoimmune', 'Autoimmune', 'rest', ['tauto01', 'tauto02'], 'autoimmune health'),
  topic('bones', 'Bone strength', 'movement', ['tmove05', 'm43b']),
  topic('pelvic', 'Pelvic floor', 'movement', ['tmove07', 'tpp03']),
]);
/** Everyday health words a member may TAP as a topic and read back in "You said …". They stay on CLAIM_BLOCKLIST everywhere else: no supplement row, product name or plan sentence may use them (tests prove it). */
export const TOPIC_TERMS_ALLOWED = Object.freeze(['thyroid', 'blood pressure', 'postpartum', 'autoimmune']);
/** Lessons whose titles name a product, a supplement, a device or a medicine: never in a topic list, never "For you" by topic list. */
export const NEVER_HIGHLIGHT = Object.freeze(['tgut10', 'thormones11', 'tmove10', 'tenergy09', 'tenergy10', 'tsleep08', 'tsleep09', 'm21b']);

const doing = (id, label) => Object.freeze({ id, label });
/** What she already does. Practitioner view only: the app never reads it for anything else. */
export const INTAKE_DOING = Object.freeze([
  doing('walks', 'Walks'), doing('stretch', 'Stretching or yoga'), doing('quiet', 'Quiet time or prayer'), doing('teas', 'Herbal teas'),
  doing('supplements', 'Supplements'), doing('provider', 'Seeing a provider'), doing('nothing', 'Nothing yet'),
]);
export const INTAKE_MEDICINES = Object.freeze(['none', 'otc', 'prescribed', 'both']);
/** Member facing labels for the medicine answer (the stored values above are not member copy). */
export const MEDICINE_LABELS = Object.freeze({ none: 'No', otc: 'Over the counter', prescribed: 'From a doctor', both: 'Both' });

const lower = (s) => s.toLowerCase();
const list = (xs) => xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;

/** Every word a member reads that this feature adds, in one place (validated and reviewed). */
export const INTAKE_COPY = Object.freeze({
  topicsTitle: 'What is on your mind?',
  topicsHelp: 'Pick up to 3. Skip if you like. We use this to shape your first plan.',
  moreTitle: 'A little more about you',
  doingTitle: 'What do you already do for it?',
  doingHelp: 'Pick any that fit. Skip if none do.',
  medsTitle: 'One more thing. Do you take any medicines?',
  medsHelp: 'Yes or no is enough. We never give advice on them.',
  medsYes: 'Yes', medsNo: 'No', medsKind: 'Which kind?',
  noteTitle: 'Want to add more?',
  noteHelp: 'A note for your practitioner. Only they read it. You can leave it blank.',
  notePlaceholder: 'Type here',
  skip: 'Skip for now', next: 'Continue', save: 'Save my answers', saved: 'Saved.',
  /** The starting plan (INTAKE and SCAN_PLAN, before her first scan). */
  planTitle: 'Your starting plan',
  planIntro: 'Small habits to try, picked from what you said. Do what feels good. Skip the rest.',
  planFooter: (dateLabel) => `Your starting plan, from ${dateLabel}. Your first scan brings a new plan. Wellness education, not medical care.`,
  why: (label) => `You said ${lower(label)} is on your mind. Here are small habits to try.`,
  whyMany: (labels) => `You said these are on your mind: ${list(labels.map(lower))}. Here are small habits to try.`,
  homeCard: 'Your starting plan is ready',
  homeSub: 'Small habits, picked from what you said',
  deptStatus: 'Starting plan ready',
  forYouTag: 'For you',
  /** Profile card and the invite card for members who joined before intake existed. */
  profileTitle: 'What brings you here',
  profileNone: 'Not answered yet',
  profileNote: 'Your note is saved. Only your practitioner reads it.',
  profileEdit: 'Update my answers',
  onMind: 'On your mind', alreadyDo: 'Already do', medicines: 'Medicines',
  inviteTitle: 'What brings you here?',
  inviteBody: 'Tell us what is on your mind. It helps us point out lessons for you.',
  inviteCta: 'Answer now', inviteSkip: 'Not now',
  /** Home check in, 30 days after her answers or after a new scan. */
  checkTitle: 'What is new on your mind?',
  checkBody: 'Things change. Tell us what is on your mind now.',
  /** Topic lessons (TOPIC_LESSONS=1): the Paths row and the page it opens. */
  lessonsTitle: 'Lessons for your topics',
  lessonsIntro: 'Short reads on what is on your mind. Read any that fits you.',
  lessonsRow: 'Lessons for your topics',
  lessonsRowSub: 'Short reads on what is on your mind',
  lessonsMine: 'You picked this',
  lessonsNone: 'No topic lessons in your plan yet.',
});
/** Short labels measured for words, not reading grade (like the plan tags). */
export const INTAKE_TAGS = Object.freeze(['medsYes', 'medsNo', 'medsKind', 'notePlaceholder', 'skip', 'next', 'save', 'saved', 'deptStatus', 'forYouTag', 'profileNone', 'profileEdit', 'onMind', 'alreadyDo', 'medicines', 'inviteCta', 'inviteSkip', 'lessonsTitle', 'lessonsRow', 'lessonsMine']);

/** Plain words for the staff review page, checked like HOW_BUILT. */
export const INTAKE_HOW_BUILT = Object.freeze([
  'She answers at sign up. Each question can be skipped. She can change her answers on her Profile.',
  `Her topics come from a fixed list. There are ${INTAKE_TOPICS.length} topics in all. Each topic points to one plan area.`,
  'With no scan yet, her topics build her starting plan. Each topic counts as elevated. It uses the same lists as the scan plan. It follows the same rules.',
  'Day 1 of the starting plan is her first day. The plan never shows a trend line. Her first scan replaces it. Nothing she did is lost.',
  'She earns 25 blooms when the whole plan is done. That happens once.',
  `A lesson that fits her topics says "For you". It says so when it opens. It is in her topic list. Or it is in the area's first ${HIGHLIGHT_POOL_TOP}. The program itself does not change.`,
  'Only her practitioner reads her note. Marie never sees it. No model sees it. It gets the same crisis check as a Marie chat.',
  'Her medicine answer does one thing. It adds the check with your provider note. It never picks a product. Her topics never pick one.',
  'Four topics use everyday health words. She may tap them. They never appear near a product. They never appear in a plan line.',
  'Some topics have lessons of their own. They open on day 1 when she picks the topic. Everyone gets them after week 13. They stay off until approved.',
  'Home asks what is new after 30 days. It asks again after a new scan. She can say not now. Then it waits 30 more days.',
]);

export const topicById = (id) => INTAKE_TOPICS.find((t) => t.id === id) ?? null;
export const doingById = (id) => INTAKE_DOING.find((d) => d.id === id) ?? null;
/** Ids in list order, unknown ones dropped, no repeats, at most INTAKE_MAX_TOPICS. */
export const normalizeTopics = (ids) => INTAKE_TOPICS.filter((t) => Array.isArray(ids) && ids.includes(t.id)).map((t) => t.id).slice(0, INTAKE_MAX_TOPICS);
export const normalizeDoing = (ids) => INTAKE_DOING.filter((d) => Array.isArray(ids) && ids.includes(d.id)).map((d) => d.id);
/** Her topics as plan focus areas: one per area (list order), all elevated, no note. Plus the phrases each area came from (what "You said …" reads). */
export function intakeFocus(topicIds) {
  const byArea = new Map();
  for (const t of INTAKE_TOPICS) if (topicIds.includes(t.id)) byArea.set(t.area, [...(byArea.get(t.area) ?? []), t.phrase]);
  const focusAreas = [...byArea.keys()].map((areaId) => ({ area: SCAN_AREAS.find((a) => a.id === areaId).label, level: 'elevated', note: '' }));
  return { focusAreas, labelsByArea: Object.fromEntries(byArea) };
}
/** Lesson ids that say "For you" for these topics. */
export function highlightedLessons(topicIds) {
  const out = new Set();
  for (const t of INTAKE_TOPICS) {
    if (!topicIds.includes(t.id)) continue;
    for (const id of t.lessons) out.add(id);
    const area = SCAN_AREAS.find((a) => a.id === t.area);
    for (const id of area.lessons.slice(0, HIGHLIGHT_POOL_TOP)) out.add(id);
  }
  for (const id of NEVER_HIGHLIGHT) out.delete(id);
  return out;
}
export const intakeWhy = (labels) => (labels.length === 1 ? INTAKE_COPY.why(labels[0]) : INTAKE_COPY.whyMany(labels));
